import React, { useEffect, useState } from "react"
import { FaSpinner } from "react-icons/fa"

import { useEmailVerification } from "@fuse/hooks/useEmailVerification"
import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import { useSignUpModalState } from "@fuse/sharedComponents/globalQuickSignUpModal"
import InputField from "@fuse/sharedComponents/inputField"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { EButtonType, EPlatform, TWebUser } from "@letshang/constants"
import { useRecoilState } from "recoil"

import {
  defaultUser,
  updateUserStateFromBackend,
  userState,
} from "@fuse/recoil/signup"

import { auth } from "@firebaseConfig/clientApp"

import {
  getUserInfo,
  resendEmailVerificationAPI,
} from "@api/firebaseWrappedApis"

import { isDev } from "@fuse/utils/DeviceDection"
import { getWebmailUrl, validateEmail } from "@fuse/utils/emailUtils"

import { useEvents } from "@fuse/pages/eventPage/controller"

import BlurWrapper from "../blurWrapper"

const EmailComponent = ({
  closeModal,
  from = "joinEvent",
  verifyEmail = false,
  setStep,
}: {
  closeModal?: () => void
  from?: "createEvent" | "joinEvent"
  verifyEmail?: boolean
  setStep?: (step: number) => void
}) => {
  const [user, setUser] = useRecoilState(userState)
  const [email, setEmail] = useState(user?.email || "")
  const [verificationEmailSent, setVerificationEmailSent] = useState(false)
  const [emailVerified, setEmailVerified] = useState(
    user?.emailVerified || false,
  )
  const [cooldown, setCooldown] = useState(0)

  const {
    verifyEmail: verifyEmailWithHook,
    isLoading: isSendingVerificationEmail,
    startPolling,
  } = useEmailVerification()

  useEffect(() => {
    if (user?.emailVerified) {
      setEmailVerified(true)
    }
  }, [user])

  const handleSendVerificationEmail = async () => {
    if (!validateAndShowErrors()) return
    if (cooldown > 0) return

    const result = await verifyEmailWithHook(email, {
      onSuccess: () => {
        setVerificationEmailSent(true)
      },
      onCloseUI: () => {
        // Dev mode: immediate verification, update local state
        setEmailVerified(true)
        setVerificationEmailSent(true)
      },
      onRequireInboxCheck: () => {
        setVerificationEmailSent(true)
        startPolling(() => {
          handleCreateEvent()
        })
      },
      onVerificationComplete: () => {
        // Update local state when verification completes
        setEmailVerified(true)
      },
    })
    return result
  }

  const validateAndShowErrors = () => {
    if (!email || !email.trim()) {
      showErrorToast("Please enter a valid email")
      return false
    }
    if (!validateEmail(email)) {
      showErrorToast("Please enter a valid email")
      return false
    }

    return true
  }

  const [isLoading, setIsLoading] = useState(false)

  const { setFastSignUpStage, setEventParams } = useSignUpModalState()
  const { plusOnePopupState, setPlusOnePopupState, plusOnes } = useEvents(
    user.eventId,
  )
  const [isResendLoading, setIsResendLoading] = useState(false)

  const handleCreateEvent = async () => {
    try {
      setIsLoading(true)

      await updateUserStateFromBackend()
      setUser((prev: TWebUser) => ({
        ...prev,
        isOnboarded: true,
        isLoggedIn: true,
      }))
      setEventParams({ plusOneCount: plusOnePopupState.plusOneCount })
      setFastSignUpStage("done")
      closeModal()
    } catch (e) {
      console.error(e)
      showErrorToast(
        "Unexpected error occurred, please close this window and try again. If the problem persists, please contact support.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  const onResendMail = () => {
    setIsResendLoading(true)
    resendEmailVerificationAPI({})
      .then(() => {
        showSuccessToast("Mail sent successfully!")
      })
      .finally(() => {
        setIsResendLoading(false)
      })
  }

  return (
    <>
      <div className="relative z-[10] ">{isLoading && <LoadingSpinner />}</div>
      <div className="flex gap-6 flex-col">
        {!verificationEmailSent ? (
          <BlurWrapper
            className="px-7 py-14 md:px-14 md:py-14 rounded-tr-3xl rounded-tl-3xl md:rounded-xl flex flex-col gap-6"
            overlayColorDefault="dark"
            overlayOpacityDefault="60"
          >
            <div className="text-left">
              <div className="font-sans text-3xl font-semibold text-white text-center -mt-6">
                Secure your account
                <p className="text-white/70 mt-3 text-sm leading-relaxed text-left font-normal">
                  Your email is necessary for us to stay connected and share
                  updates straight to your inbox.
                </p>
              </div>
              <div className="text-white mb-2 mt-4">Email</div>
              <div className="flex gap-2 mb-2">
                <input
                  type="email"
                  placeholder="names@email.com"
                  className="flex-1 rounded-lg bg-white/20 px-4 py-3 text-white placeholder-white/40 outline-none border-none"
                  value={email}
                  disabled={isSendingVerificationEmail}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="text-white/50 mb-4 px-1">
                {verificationEmailSent && !emailVerified && email
                  ? "We've sent a verification link to your email. Just click it to confirm your account. Make sure to check your spam"
                  : ""}
              </div>
            </div>
            {!verificationEmailSent && (
              <button
                className="rounded-lg bg-white/20 px-10 py-3 text-white font-semibold disabled:opacity-50"
                disabled={!email}
                onClick={handleSendVerificationEmail}
              >
                {isSendingVerificationEmail ? (
                  <div className="flex items-center justify-center">
                    <FaSpinner className="animate-spin mr-2" size={16} />
                    Verifying...
                  </div>
                ) : (
                  "Verify"
                )}
              </button>
            )}
          </BlurWrapper>
        ) : (
          <BlurWrapper
            className="px-7 py-14 md:px-14 md:py-14 rounded-tr-3xl rounded-tl-3xl md:rounded-xl flex flex-col gap-6"
            overlayColorDefault="dark"
            overlayOpacityDefault="60"
          >
            {/* Title */}
            <div className="text-center">
              <h2 className="text-white text-2xl font-semibold">
                Verify your email
              </h2>

              <p className="text-white/70 mt-3 text-sm leading-relaxed">
                We sent an email to <br />
                <span className="font-semibold text-white">{email}</span> <br />
                <br />
                Just click on the button in that email to complete your
                verification.
              </p>
            </div>

            <div className="w-full flex items-center justify-center">
              <Button
                className="w-full bg-white/10 text-white py-3 rounded-full transition text-sm max-w-72 hover:bg-white/30 disabled:opacity-50"
                onClick={() => {
                  const webmailUrl = getWebmailUrl(email)
                  window.open(webmailUrl, "_blank")
                }}
                disabled={isResendLoading}
              >
                Open email app
              </Button>
            </div>

            <div className="text-center">
              <p className="text-white/70 text-sm">
                Haven't received email?{" "}
                <span
                  className="text-white underline cursor-pointer hover:text-white/80"
                  onClick={onResendMail}
                >
                  {isResendLoading ? "Sending..." : "Resend"}
                </span>
              </p>
            </div>

            <p className="text-white/35 text-xs text-center leading-relaxed">
              Didn't receive email? Check your spam folder,
              <br />
              or try another email address
            </p>
          </BlurWrapper>
        )}
      </div>
    </>
  )
}

export default EmailComponent
